import { useDispatch } from "react-redux"
import { clearConfirmTypeModal, setConfirmTypeModal } from "../../features/gms-state-and-modal/modal-slice";
import { removeAllNewFilesDocuments, setIsUpdateDocuments } from "../../features/actuarial/document-slice";
import { useProposalData } from "../populate-data/use-proposal-data";
import { setIsEditingNotesForProposal, setIsEditRemarks, setNoteForProposalAct } from "../../features/actuarial/remark-slice";
import { DocumentFile } from "../../features/actuarial/document-state";
import { useToastify } from "../../library/toastify/toast-provider";
import { useUploadFileDocumentsMutation } from "../../api/files/files-api";
import { useActuarialNotesMutation, useActuarialNotesToCFEMutation, useComputeEvidenceNotesMutation, useComputeRatesMutation, useComputeTotalAmountPremiumMutation, useDownloadRatesTemplateMutation, useReleaseProposalMutation, useUploadRatesExcelMutation } from "../../api/actuarial/actuarial-api";
import { setAmountWarning, setBulkData, setIsUpdateRates } from "../../features/actuarial/rates-slice";
import { useIsCurrentUserRateEditor } from "../proposal/use-state-checker-proposal";
import { useRef, useState } from "react";
import { setIsUpdateAnnualPremiumAndIoE } from "../../features/actuarial/annual-premium-eoi.slice";
import { useProposalTypeData } from "../proposal/use-proposal-type-data";
import { useRatesValidation } from "../validations/use-rates-validation";
import { useComputeRates } from "../rates/use-compute-rate";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { useProposalComputation } from "../proposal/use-proposal-computation";
import { useModalAccess } from "../../components/global/modal/use-modal-access";

export const useComputationActions = () => {

    const { showToastNotify } = useToastify();
    const dispatch = useDispatch();
    const { currentProposalId } = useModalAccess();
    const { 
        isReleaseProposal,
        isBookedProposal,
        isClosedProposal,
    } = useProposalTypeData();
    const {
        annualPremiumAmount,
        evidenceNotes: evidence,
        documents: documentFiles,
        actNoteForCFE: noteForCFE
    } = useProposalComputation()

    const isCurrentEditor = useIsCurrentUserRateEditor();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedRatesFile, setSelectedRatesFile] = useState<File | null>(null);
    const [isUploadingRates, setIsUploadingRates] = useState(false);
    const disabled = isReleaseProposal || isBookedProposal;
    const cantEdit = isBookedProposal || isClosedProposal || !isCurrentEditor;

    const { validateRates, validateTotalAnnualPremium, validateEoI } = useRatesValidation();
    const { formattedFinalAllRates } = useComputeRates() ?? {};

    const { 
        populateProposalActuarialFiles, 
        populateProposalNotesForPDF, 
        populateProposalNotesForCFE,
        // populateRates, 
        // repopulateRates, 
        populateRates,
        // repopulateAll, 
        populateEvidenceNotes, 
        populateTotalAmount,
        repopulateAll,
        repopulateRates,
    } = useProposalData();

    const [computeTotalAmountPremium, { isLoading: isLoadingComputeTotalAmountPremium }] = useComputeTotalAmountPremiumMutation();
    const [evidenceNotes, { isLoading: isLoadingEvidenceNotes }] = useComputeEvidenceNotesMutation();
    const [releaseProposal, { isLoading: isLoadingReleaseProposal }] = useReleaseProposalMutation();
    const [computeRates, { isLoading: isLoadingComputeRates }] = useComputeRatesMutation();
    const [downloadRatesTemplate, { isLoading: isLoadingDownloadRatesTemplate }] = useDownloadRatesTemplateMutation();
    const [uploadRatesExcel, { isLoading: isLoadingUploadRatesExcel }] = useUploadRatesExcelMutation();
    // const [downloadMasterAttachedFile, { isLoading: isLoadingDownloadMasterAttachedFile }] = useDownloadMasterFileCSVFileMutation();
    // const [downloadSupportingDocuments, { isLoading: isLoadingDownloadSupportingDocuments }] = useDownloadSupportingDocumentsMutation();
    // const [downloadActDocuments, { isLoading: isLoadingDownloadActDocuments }] = useDownloadFileDocumentsMutation();
    
    const [uploadDocuments, { isLoading: isLoadingUploadDocuments}] = useUploadFileDocumentsMutation();
    const [saveNotes, { isLoading: isLoadingSaveNotes }] = useActuarialNotesMutation();
    const [saveNotesToCFE, { isLoading: isLoadingSaveNotesToCFE }] = useActuarialNotesToCFEMutation();

    const handleIsUpdateRates = () => {
        if(!isCurrentEditor) {
            showToastNotify("You are not authorized to update.", "warning");
            return
        }
        dispatch(setIsUpdateRates(true))
    }

    const handleIsUploadRates = () => {
        fileInputRef.current?.click();
    };

    const handleCancelUpdateRates = () => {
        setSelectedRatesFile(null);
        setIsUploadingRates(false);
        dispatch(setIsUpdateRates(false))
        dispatch(setAmountWarning({ rateType: 'rates1865', warning: null }));
        dispatch(setAmountWarning({ rateType: 'rates6670', warning: null }));
        dispatch(setAmountWarning({ rateType: 'rates7175', warning: null }));
        dispatch(setAmountWarning({ rateType: 'rates7680', warning: null }));
        dispatch(setBulkData({
            isBulkFillOpen: false,
            activeAgeBracket: null,
            selectedMonths: [],
            bulkValue: 'N/A',
            bulkWarning: null,
        }));
        populateRates();
    }

    const handleIsUpdateTotalAmountPremium = () => {
        dispatch(setIsUpdateAnnualPremiumAndIoE({ isUpdate: true, type: 'annualPremium' }))
        dispatch(setIsUpdateAnnualPremiumAndIoE({ isUpdate: false, type: "evidenceOfInsurability", }));
        populateEvidenceNotes()
    }

    const handleCancelUpdateTotalAmountPremium = () => {
        dispatch(setIsUpdateAnnualPremiumAndIoE({ isUpdate: false, type: "annualPremium", }));
        populateTotalAmount();
    }

    const handleIsUpdateEvidence = () => {
        if(!isCurrentEditor) {
            showToastNotify("You are not authorized to update.", "warning");
            return
        }
        dispatch(setIsUpdateAnnualPremiumAndIoE({ isUpdate: true, type: 'evidenceOfInsurability' }))
        // dispatch(setIsUpdateAnnualPremiumAndIoE({ isUpdate: false, type: "annualPremium", }));
        // populateTotalAmount();
    }

    const handleCancelUpdateEvidence = () => {
        dispatch(setIsUpdateAnnualPremiumAndIoE({ isUpdate: false, type: "evidenceOfInsurability", }));
        populateEvidenceNotes()
    }

    const handleShowModalConfirmRelease = () => {
        if(disabled) return
        // if(isAmend)// if amend is true ignore the if(!isApproved) line code, direct to dispatch
        // if(!isApproved) return showToastNotify('Please complete the rates, amount, evidences of insurability, and ensure the proposal is approved before releasing.', 'warning')
        // if (!isAmend && !isApproved) {
        //     return showToastNotify(
        //         'Please complete the rates, amount, evidences of insurability, and ensure the proposal is approved before releasing.',
        //         'warning'
        //     );
        // }
        dispatch(setConfirmTypeModal('isConfirmReleaseProposal'))
    }
    
    const handleCloseModalRelease = () => {
        dispatch(clearConfirmTypeModal())
    }
    
    const handleSaveRates = async () => {
        // if(isNoRates) return
        if(!currentProposalId) return;
        try {
            if (!validateRates()) return;
            const response = await computeRates({ proposalId: currentProposalId, body: formattedFinalAllRates, method: 'PUT'}).unwrap();
            showToastNotify(response.message, 'success');
            dispatch(setIsUpdateRates(false))
            dispatch(setAmountWarning({ rateType: 'rates1865', warning: null }));
            dispatch(setAmountWarning({ rateType: 'rates6670', warning: null }));
            dispatch(setAmountWarning({ rateType: 'rates7175', warning: null }));
            dispatch(setAmountWarning({ rateType: 'rates7680', warning: null }));

            dispatch(setBulkData({
                isBulkFillOpen: false,
                activeAgeBracket: null,
                selectedMonths: [],
                bulkValue: 'N/A',
                bulkWarning: null,
            }));
        } catch (error: any) {
            const errorMessage = error.errors?.[0]?.msg || error.message;
            showToastNotify(errorMessage, 'error');
        } 
    }

    const handleDownloadRatesTemplate = async () => {
        if(!currentProposalId) return
        try {
            const blob = await downloadRatesTemplate({
                proposalId: currentProposalId,
            }).unwrap();

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = "Rates-Template.xlsx";
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            const errorMessage = error.errors?.[0]?.msg || error.message;
            showToastNotify(errorMessage, 'error');
        }
    }

    const handleSelectRatesFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (!file) return;

        const allowedExtensions = ['xlsx', 'xls', 'csv'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
            showToastNotify('Invalid file type. Please select an Excel (.xlsx, .xls) or CSV (.csv) file.', 'error');

            e.target.value = '';
            setSelectedRatesFile(null);
            return;
        }

        setSelectedRatesFile(file);
    };

    // const handleUploadFileRates = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const handleUploadFileRates = async () => {
        // const file = e.target.files?.[0];

        // if (!file || !currentProposalId) return;

        // try {
        //     const response = await uploadRatesExcel({ proposalId: currentProposalId, excelFile: file, }).unwrap();
        //     populateRates()
        //     showToastNotify(response.message, 'success');
        // } catch (error: any) {
        //     const errorMessage = error.errors?.[0]?.msg || error.message;
        //     showToastNotify(`${errorMessage}`, 'error')
        // }
        if (!selectedRatesFile || !currentProposalId) return;

        try {
            setIsUploadingRates(true);

            const response = await uploadRatesExcel({
                proposalId: currentProposalId,
                excelFile: selectedRatesFile,
            }).unwrap();

            // populateRates();

            showToastNotify(response.message, 'success');

            setSelectedRatesFile(null);

            // Reset file input so the same file can be selected again
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error: any) {
            const errorMessage = error.errors?.[0]?.msg || error.message;
            showToastNotify(errorMessage, 'error');
        } finally {
            setIsUploadingRates(false);
        }
    }

    const handleReleaseProposal = async () => {
        if(!currentProposalId) return
        try {
            await releaseProposal(currentProposalId).unwrap()
            showToastNotify('Proposal has been successfully released.', 'success');
            dispatch(clearConfirmTypeModal())
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    }

    const handleSaveTotalAmountPremium = async () => {
        // if(isNoRates) return
        if(!currentProposalId) return;
        try {
            if (!validateTotalAnnualPremium()) return;
            if(!annualPremiumAmount) return;
            const response = await computeTotalAmountPremium({ 
                proposalId: currentProposalId, 
                total_annual_premium: annualPremiumAmount
            }).unwrap();
            showToastNotify(response.message, 'success');
            dispatch(setIsUpdateAnnualPremiumAndIoE({isUpdate: false, type: 'annualPremium'}))
        } catch (error: any) {
            const errorMessage = error.errors?.[0]?.msg || "An unexpected error occurred";
            showToastNotify(errorMessage, 'error');
        }
    }

    const handleSaveEvidence = async () => {
        if(!currentProposalId) return;
        try {
            if (!validateEoI()) return;
            const response = await evidenceNotes({ proposalId: currentProposalId, evidence_notes: evidence }).unwrap();
            showToastNotify(response.message, 'success');
            dispatch(dispatch(setIsUpdateAnnualPremiumAndIoE({isUpdate: false, type: 'evidenceOfInsurability'})))
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    }

    // --------------------------------------- //


    const handleSaveDocuments = async () => {
        const files = documentFiles
            .filter((item): item is DocumentFile & { file: File } =>
                item.type === "new" && item.file != null
            )
            .map(item => item.file);
        if(files.length === 0) return
        if(!currentProposalId) return
        try {
            await uploadDocuments({ proposalID: currentProposalId, files: files }).unwrap()
            dispatch(setIsUpdateDocuments(false))
        } catch (error: any) {
            const errorMsg = error.error;
            const errorMessage = error.errors?.[0]?.msg;
            const errorMessageFile = error.data?.errors?.[0]?.msg;
            const isErrorMessage = errorMsg || errorMessage || errorMessageFile || "An unexpected error occurred"
            showToastNotify(isErrorMessage, 'error')
        }
    }

    const handleCancelSaveDocuments = () => {
        dispatch(setIsUpdateDocuments(false))
        dispatch(removeAllNewFilesDocuments())
        populateProposalActuarialFiles()
    }

    const handleSaveNotesForProposal = async (notes: string | null, showInPdf: boolean,) => {
    if (!currentProposalId) return ;

    if (showInPdf && !notes?.trim()) {
        showToastNotify("Notes for proposal are required.", "warning");
        return ;
    }

    try {
        const response = await saveNotes({
            method: "POST",
            proposalId: currentProposalId,
            actuarial_notes: notes ?? "",
            show_in_pdf: showInPdf,
        }).unwrap();

        dispatch(setIsEditRemarks({
            type: "proposal",
            value: false,
        }));

        dispatch(setIsEditingNotesForProposal(false));

        dispatch(setNoteForProposalAct({
            isShow: false,
            notesProposal: "",
            showCKEdit: false,
            copyNotes: "",
        }));
        // populateProposalNotesForPDF()
        // repopulateAll();
        // repopulateRates();
        showToastNotify(response.message, "success");

        // return true;
    } catch (error: any) {
        const errorMsg = error.error;
        const errorMessage = error.errors?.[0]?.msg;
        const errorMessageFile = error.data?.errors?.[0]?.msg;

        const isErrorMessage =
            errorMsg ||
            errorMessage ||
            errorMessageFile ||
            "An unexpected error occurred";

        showToastNotify(isErrorMessage, "error");

        // return false;
    }
};

    const handleSaveNotesForCFE = async () => {
        if(!currentProposalId) return
        try {
            const response = await saveNotesToCFE({ method: 'POST', proposalId: currentProposalId, notes: noteForCFE }).unwrap()
            dispatch(setIsEditRemarks({type: 'cfe', value: false}))
            showToastNotify(response.message, 'success')
        } catch (error: any) {
            const errorMsg = error.error;
            const errorMessage = error.errors?.[0]?.msg;
            const errorMessageFile = error.data?.errors?.[0]?.msg;
            const isErrorMessage = errorMsg || errorMessage || errorMessageFile || "An unexpected error occurred"
            showToastNotify(isErrorMessage, 'error')
        }
    }

    const handleIsEditNotesForProposal = (type: 'proposal' | 'cfe') => {
        if(type === 'cfe') {
            dispatch(setIsEditRemarks({type: 'cfe', value: true}))
            return;
        }
        if(type === 'proposal') {
            dispatch(setIsEditRemarks({type: 'proposal', value: true}));
            return;
        }
    }

    const handleCancelSaveNotesForProposal = (type: 'cfe' | 'proposal') => {
        if(type === 'cfe') {
            dispatch(setIsEditRemarks({type: 'cfe', value: false}))
            populateProposalNotesForCFE()
            return;
        }
        if(type === 'proposal') {
            dispatch(setIsEditRemarks({type: 'proposal', value: false}))
            populateProposalNotesForPDF()
            return;
        }
    }

    return {
        cantEdit,
        handleCancelUpdateRates,
        handleSaveRates,
        handleIsUpdateRates,
        handleDownloadRatesTemplate,
        fileInputRef,
        handleIsUploadRates,
        handleUploadFileRates, 
        selectedRatesFile, 
        isUploadingRates, 
        handleSelectRatesFile,

        handleSaveTotalAmountPremium,
        handleIsUpdateTotalAmountPremium,
        handleCancelUpdateTotalAmountPremium,

        handleSaveEvidence,
        handleCancelUpdateEvidence,
        handleIsUpdateEvidence,

        handleSaveDocuments,
        handleCancelSaveDocuments,

        handleSaveNotesForProposal, 
        handleCancelSaveNotesForProposal, 
        handleIsEditNotesForProposal,
        handleSaveNotesForCFE, 

        handleShowModalConfirmRelease,

        handleReleaseProposal,
        handleCloseModalRelease,

        // downloadMasterAttachedFile,
        // downloadSupportingDocuments,
        // downloadActDocuments,

        isLoadingComputeRates,
        isLoadingComputeTotalAmountPremium,
        isLoadingEvidenceNotes,
        isLoadingUploadDocuments,
        isLoadingSaveNotes,
        isLoadingSaveNotesToCFE,
        isLoadingReleaseProposal,
        isLoadingDownloadRatesTemplate,
        isLoadingUploadRatesExcel,
        // isLoadingDownloadMasterAttachedFile,
        // isLoadingDownloadSupportingDocuments,
        // isLoadingDownloadActDocuments,
    }
}